import { useAnimate } from 'motion/react'
import { useCallback, useEffect, useRef, useState } from 'react'

export type RevealStep = {
  selector: string
  at: number
}

type StepState = 'hidden' | 'animating' | 'done'
const REVEAL_DURATION_SECONDS = 1

export function useParagraphReveal({
  steps,
  skipNonce,
  enabled = true,
  onComplete,
}: {
  steps: readonly RevealStep[]
  skipNonce: number
  enabled?: boolean
  onComplete?: () => void
}) {
  const [scope, animate] = useAnimate()
  const [doneCount, setDoneCount] = useState(0)
  const initialSkipNonce = useRef(skipNonce)
  const stepStatesRef = useRef<StepState[]>(steps.map(() => 'hidden'))
  const timersRef = useRef<number[]>([])
  const doneCountRef = useRef(0)
  const didNotifyCompleteRef = useRef(false)
  const timelineStartMsRef = useRef<number | null>(null)

  const clearPendingTimers = useCallback(() => {
    for (const timer of timersRef.current) {
      window.clearTimeout(timer)
    }
    timersRef.current = []
  }, [])

  const markDone = useCallback((index: number) => {
    if (stepStatesRef.current[index] === 'done') return

    stepStatesRef.current[index] = 'done'
    doneCountRef.current += 1
    setDoneCount(doneCountRef.current)
  }, [])

  const revealStep = useCallback(
    async (index: number, duration: number) => {
      if (stepStatesRef.current[index] === 'done') return
      if (stepStatesRef.current[index] === 'hidden') {
        stepStatesRef.current[index] = 'animating'
      }

      await animate(steps[index].selector, { opacity: 1 }, { duration })

      if (stepStatesRef.current[index] === 'animating') {
        markDone(index)
      }
    },
    [animate, markDone, steps],
  )

  const schedulePendingReveals = useCallback(() => {
    if (!enabled) return
    if (timelineStartMsRef.current === null) return

    clearPendingTimers()

    const now = Date.now()

    for (const [index, step] of steps.entries()) {
      if (stepStatesRef.current[index] !== 'hidden') continue

      const targetAtMs = timelineStartMsRef.current + step.at * 1000
      const delayMs = Math.max(0, targetAtMs - now)

      const timer = window.setTimeout(() => {
        void revealStep(index, REVEAL_DURATION_SECONDS)
      }, delayMs)

      timersRef.current.push(timer)
    }
  }, [clearPendingTimers, enabled, revealStep, steps])

  const skipOne = useCallback(() => {
    if (!enabled) return

    let stepIndex = stepStatesRef.current.findIndex(
      (stepState) => stepState === 'animating',
    )
    let state: StepState = 'animating'

    if (stepIndex === -1) {
      stepIndex = stepStatesRef.current.findIndex(
        (stepState) => stepState === 'hidden',
      )
      state = 'hidden'
    }

    if (stepIndex === -1) return

    const targetTimelineSeconds =
      steps[stepIndex].at +
      (state === 'animating' ? REVEAL_DURATION_SECONDS : 0)

    const desiredTimelineStartMs = Date.now() - targetTimelineSeconds * 1000
    if (
      timelineStartMsRef.current === null ||
      desiredTimelineStartMs < timelineStartMsRef.current
    ) {
      timelineStartMsRef.current = desiredTimelineStartMs
    }

    void revealStep(stepIndex, 0)
    schedulePendingReveals()
  }, [enabled, revealStep, schedulePendingReveals, steps])

  useEffect(() => {
    if (!enabled) {
      clearPendingTimers()
      return
    }

    if (timelineStartMsRef.current === null) {
      timelineStartMsRef.current = Date.now()
    }

    schedulePendingReveals()

    return clearPendingTimers
  }, [clearPendingTimers, enabled, schedulePendingReveals])

  useEffect(() => {
    if (!enabled) return
    if (skipNonce === initialSkipNonce.current) return

    initialSkipNonce.current = skipNonce
    skipOne()
  }, [enabled, skipNonce, skipOne])

  useEffect(() => {
    if (doneCount !== steps.length) return
    if (didNotifyCompleteRef.current) return

    clearPendingTimers()
    didNotifyCompleteRef.current = true
    onComplete?.()
  }, [clearPendingTimers, doneCount, onComplete, steps.length])

  return {
    scope,
  }
}
